import React, {useState} from 'react';
import useRemoveGroupMember, {
    RemoveGroupMemberCommand
} from '../../api/groups/useRemoveGroupMember';
import useUpdateGroupMember, {
    UpdateGroupMemberCommand
} from '../../api/groups/useUpdateGroupMember';
import useUpdateGroupMemberInfo, {
    UpdateGroupMemberInfoCommand
} from '../../api/groups/useUpdateGroupMemberInfo';
import GroupMemberActionIcons from './GroupMemberActionIcons';
import {
    CollectionInfoModel,
    GroupMemberInfoAnswerModel,
    GroupMemberInfoFieldModel,
    GroupMemberModel,
    IGroupMemberInfoAnswer,
    UserCollectionType
} from '../../api/types.ts';
import {toast} from 'react-toastify';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import EnumSelect from '../shared/EnumSelect.tsx';
import GroupMemberInfoField from '../shared/GroupMemberInfoField.tsx';
import ConfirmButton from '../shared/ConfirmButton.tsx';
import displayName from '../../lib/displayName.ts';
import dateTimeUtcNow from '../../lib/dateTimeUtcNow.ts';
import {useMutation} from 'react-query';
import unknownErrorToString from '../../lib/unknownErrorToString.ts';
import {isNotNullOrWhiteSpace} from '../../lib/isNullOrWhiteSpace.ts';
import OverlayWithSpinner from '../shared/OverlayWithSpinner.tsx';
import {Avatar} from '../ds/CreatorIdentity.tsx';
import atify from '../../lib/atify.ts';

interface GroupMemberCardProps {
    collection: CollectionInfoModel;
    member: GroupMemberModel;
    onChanged: () => Promise<void>;
    canEdit: boolean;
    fields: GroupMemberInfoFieldModel[];
}

const GroupMemberCard: React.FC<GroupMemberCardProps> = ({
    collection,
    member,
    onChanged,
    canEdit,
    fields
}) => {
    const [showModal, setShowModal] = useState(false);
    const [localMember, setLocalMember] = useState(member);
    const removeGroupMember = useRemoveGroupMember();
    const updateGroupMember = useUpdateGroupMember();
    const updateGroupMemberInfo = useUpdateGroupMemberInfo();

    const removeGroupMemberMutation = useMutation(removeGroupMember, {
        onSuccess: () => {
            toast.success('Member removed');
            setShowModal(false);
            onChanged();
        },
        onError: (error: unknown) => {
            toast.error(
                'Failed to remove member ' + unknownErrorToString(error)
            );
        }
    });

    const updateGroupMemberMutation = useMutation(updateGroupMember, {
        onSuccess: () => {
            toast.success('Member updated');
            setShowModal(false);
            onChanged();
        },
        onError: (error: unknown) => {
            toast.error(
                'Failed to update member ' + unknownErrorToString(error)
            );
        }
    });

    const updateGroupMemberInfoMutation = useMutation(updateGroupMemberInfo, {
        onSuccess: () => {
            toast.success('Member updated');
            setShowModal(false);
            onChanged();
        },
        onError: (error: unknown) => {
            toast.error(
                'Failed to update member info ' + unknownErrorToString(error)
            );
        }
    });

    const handleRemoveMember = async () => {
        const command: RemoveGroupMemberCommand = {
            collectionId: collection.id!,
            userId: localMember.userId
        };

        await removeGroupMemberMutation.mutateAsync(command);
    };

    const handleUpdate = async () => {
        const updateMemberCommand: UpdateGroupMemberCommand = {
            collectionId: collection.id!,
            userId: localMember.userId,
            userCollectionType: localMember.userCollectionType,
            membershipStatus: localMember.membershipStatus
        };
        const updateInfoCommand: UpdateGroupMemberInfoCommand = {
            collectionId: collection.id!,
            userId: localMember.userId,
            answers: localMember.groupMemberInfoAnswers
        };

        await updateGroupMemberMutation.mutateAsync(updateMemberCommand);
        await updateGroupMemberInfoMutation.mutateAsync(updateInfoCommand);
    };

    const userHasName = () => {
        return isNotNullOrWhiteSpace(localMember.name);
    };

    const handleMemberInfoAnswerChange = (
        answer:
            | ((prevState: IGroupMemberInfoAnswer) => IGroupMemberInfoAnswer)
            | IGroupMemberInfoAnswer
    ) => {
        setLocalMember(prevMember => ({
            ...prevMember,
            groupMemberInfoAnswers: prevMember.groupMemberInfoAnswers.map(a => {
                if (typeof answer === 'function') {
                    const updatedAnswer = answer(
                        a
                    ) as GroupMemberInfoAnswerModel;
                    if (
                        a.groupMemberInfoFieldId ===
                        updatedAnswer.groupMemberInfoFieldId
                    ) {
                        return updatedAnswer as GroupMemberInfoAnswerModel;
                    }
                } else if (
                    a.groupMemberInfoFieldId ===
                    (answer as GroupMemberInfoAnswerModel)
                        .groupMemberInfoFieldId
                ) {
                    return answer as GroupMemberInfoAnswerModel;
                }
                return a;
            })
        }));
    };

    return (
        <>
            {(removeGroupMemberMutation.isLoading ||
                updateGroupMemberMutation.isLoading ||
                updateGroupMemberInfoMutation.isLoading) && (
                <OverlayWithSpinner />
            )}
            <div className="mx-person-row">
                <Avatar
                    creator={{
                        nickname: localMember.nickname,
                        name: localMember.name,
                        thumbnail: localMember.thumbnail
                    }}
                    size="m"
                />
                <span className="mx-person-text">
                    <span className="mx-person-name">
                        {userHasName() ? localMember.name : atify(localMember.nickname)}
                    </span>
                    <span className="mx-person-detail">
                        {userHasName() && `${atify(localMember.nickname)} · `}
                        {UserCollectionType[localMember.userCollectionType]}
                    </span>
                </span>
                {canEdit && (
                    <GroupMemberActionIcons
                        collection={collection}
                        memberUserId={localMember.userId}
                        userCollectionType={localMember.userCollectionType}
                        membership={localMember.membershipStatus}
                        edit={() => setShowModal(true)}
                        cssClass="card-actions ms-auto"
                        showText={false}
                        onChanged={onChanged}
                    />
                )}
            </div>
            {showModal && (
                <Modal
                    id="EditGroupMember"
                    toggle={() => setShowModal(false)}
                    isOpen={true}
                >
                    <ModalHeader>Edit member</ModalHeader>
                    <ModalBody>
                        {localMember.thumbnail && (
                            <img
                                src={localMember.thumbnail}
                                crossOrigin="anonymous"
                                alt={localMember.nickname}
                                className="mb-3 w-100"
                            />
                        )}
                        <div className="mb-3">
                            <label htmlFor="Nickname">Handle</label>
                            <input
                                id="Nickname"
                                className="form-control"
                                value={localMember.nickname}
                                readOnly
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="Name">Name</label>
                            <input
                                id="Name"
                                className="form-control"
                                value={localMember.name}
                                readOnly
                            />
                        </div>
                        <div className="mb-3">
                            <label
                                htmlFor="UserCollectionType"
                                className="form-label"
                            >
                                Member type
                            </label>
                            <EnumSelect
                                enumType={UserCollectionType}
                                value={localMember.userCollectionType}
                                onValueChange={userCollectionType =>
                                    setLocalMember(prevMember => ({
                                        ...prevMember,
                                        userCollectionType:
                                            Number(userCollectionType)
                                    }))
                                }
                                id="UserCollectionType"
                            />
                        </div>
                        {fields.map((field, index) => {
                            const answer: GroupMemberInfoAnswerModel =
                                localMember.groupMemberInfoAnswers.find(
                                    a => a.groupMemberInfoFieldId === field.id
                                ) || {
                                    id: null,
                                    userId: localMember.userId,
                                    collectionId: collection.id!,
                                    groupMemberInfoFieldId: field.id!,
                                    createdOnUtc: dateTimeUtcNow(),
                                    updatedOnUtc: dateTimeUtcNow(),
                                    stringAnswer: null,
                                    booleanAnswer: null,
                                    numberAnswer: null,
                                    dateAnswer: null,
                                    timeAnswer: null
                                };

                            return (
                                <GroupMemberInfoField
                                    key={index}
                                    field={field}
                                    answer={answer}
                                    setAnswer={handleMemberInfoAnswerChange}
                                    index={index}
                                    readonly={false}
                                />
                            );
                        })}
                        <ConfirmButton
                            cssClass="btn btn-danger"
                            onClick={handleRemoveMember}
                            message={`Remove ${displayName(localMember)} from ${collection.name}?`}
                            body="They lose access to the group's projects and posts. They can ask to join again later."
                            confirmLabel="Remove member"
                            safeLabel="Keep member"
                        >
                            Remove from group
                        </ConfirmButton>
                    </ModalBody>
                    <ModalFooter>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setShowModal(false)}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={handleUpdate}
                        >
                            Update
                        </button>
                    </ModalFooter>
                </Modal>
            )}
        </>
    );
};

export default GroupMemberCard;
